import { useRouteContext } from '@/app/layouts/TenantLayout'
import type { NavRouteScope } from '@/lib/navRoutes'
import { toOrgSegment, toTenantSegment } from '@/lib/routeScope'
import { isPlatformUser, resolveDefaultTenantForOrg } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { useMemo } from 'react'

/**
 * Ids nav paths resolve against. On org level URLs the tenantId param is absent, so project scoped
 * links fall back to the store tenant when it belongs to the current org (a tenant from another org
 * would leak across orgs), then to the first accessible tenant in the org.
 */
export function useNavScope(): NavRouteScope {
	const { tenantId: routeTenantId, orgId: routeOrgId } = useRouteContext()
	const storeTenantId = useAuthStore(s => s.currentTenant)
	const storeOrgId = useAuthStore(s => s.currentOrganizationId)
	const user = useAuthStore(s => s.user)
	const orgId = routeOrgId || (storeOrgId ? toOrgSegment(storeOrgId) : undefined)
	const fallbackTenantId = useMemo(() => {
		if (!orgId || !user || !isPlatformUser(user)) {
			return undefined
		}
		if (storeTenantId && user.tenant_list?.some(t => t.id === storeTenantId && t.org_id === orgId)) {
			return toTenantSegment(storeTenantId)
		}
		const defaultTenantId = resolveDefaultTenantForOrg(user, orgId)
		return defaultTenantId ? toTenantSegment(defaultTenantId) : undefined
	}, [storeTenantId, orgId, user])
	const tenantId = routeTenantId || fallbackTenantId
	return useMemo(() => ({ orgId, tenantId, routeTenantId }), [orgId, tenantId, routeTenantId])
}
